import { BasePagination, Text } from '@elkaso-app/web-design';
import { useGetSiSupplierCatalogApi } from 'apis/supplier-intelligence';
import { BaseTable, BaseTableContainer } from 'components/base-table';
import {
  BaseTableSpinner,
  BaseTableTitle,
  ClearFilterButton,
  TableCell,
  TableHeader,
} from 'components/base-table/components';
import { BaseSortButton } from 'components/base-table/sorting';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import { CatalogBrandFilterButton } from 'pages/supplier-intelligence/components/filters/catalog-brand-filter-button';
import { CatalogCategoryFilterButton } from 'pages/supplier-intelligence/components/filters/catalog-category-filter-button';
import { CatalogSearchFilterButton } from 'pages/supplier-intelligence/components/filters/catalog-search-filter-button';
import { CatalogUnitFilterButton } from 'pages/supplier-intelligence/components/filters/catalog-unit-filter-button';
import React from 'react';
import { CellProps } from 'react-table';

interface ProductCatalogTabProps {
  supplierId: number;
  onProductClick: (productName: string, productIds: number[]) => void;
}

export const ProductCatalogTab = ({ supplierId, onProductClick }: ProductCatalogTabProps) => {
  const setPaginationParams = useSetPaginationParamsV1();
  const { isLoading, isFetching, data: apiData } = useGetSiSupplierCatalogApi(supplierId);
  const { pageCount, total, data: catalogRaw } = apiData ?? {};
  const catalog = React.useMemo(
    () => catalogRaw?.map((item) => ({ ...item, id: item.productIds[0] })),
    [catalogRaw]
  );

  const columns = React.useMemo(
    () => [
      {
        Header: (
          <TableHeader
            name='Product Name'
            filterButtonComponent={<CatalogSearchFilterButton />}
            sortButtonComponent={<BaseSortButton sortBy='name' />}
          />
        ),
        accessor: 'name',
        Cell: ({ value, row }: CellProps<any>) => {
          const original = row.original;
          return (
            <Text
              color='blue.600'
              fontWeight='medium'
              cursor='pointer'
              onClick={() => onProductClick(original.name, original.productIds)
              }>
              {value}
            </Text>
          );
        },
      },
      {
        Header: (
          <TableHeader
            name='Category'
            filterButtonComponent={<CatalogCategoryFilterButton />}
            sortButtonComponent={<BaseSortButton sortBy='category' />}
          />
        ),
        accessor: 'category',
        Cell: ({ value }: CellProps<any>) => <TableCell value={value} />,
      },
      {
        Header: (
          <TableHeader
            name='Brand'
            filterButtonComponent={<CatalogBrandFilterButton />}
            sortButtonComponent={<BaseSortButton sortBy='brand' />}
          />
        ),
        accessor: 'brands',
        Cell: ({ value }: CellProps<any>) => <TableCell value={value?.join(', ')} />,
      },
      {
        Header: <TableHeader name='Unit' filterButtonComponent={<CatalogUnitFilterButton />} />,
        accessor: 'unit',
        Cell: ({ value }: CellProps<any>) => <TableCell value={value} />,
      },
      {
        Header: <TableHeader name='Price (AED)' sortButtonComponent={<BaseSortButton sortBy='price' />} />,
        accessor: 'prices',
        Cell: ({ value }: CellProps<any>) => {
          const prices = value as number[];
          if (!prices?.length) return <TableCell value='—' />;
          const min = Math.min(...prices);
          const max = Math.max(...prices);
          return <TableCell value={min === max ? min.toFixed(2) : `${min.toFixed(2)} – ${max.toFixed(2)}`} />;
        },
      },
      {
        Header: <TableHeader name='Duplications' />,
        accessor: 'duplicationsCount',
        Cell: ({ value }: CellProps<any>) => <TableCell value={value ?? 0} />,
      },
    ],
    [onProductClick]
  );

  return (
    <BaseTableContainer isLoading={isLoading}>
      <BaseTableContainer.Header>
        <BaseTableContainer.Header.Left>
          <BaseTableTitle title='Product Catalog' />
          <BaseTableSpinner isFetching={isFetching} />
        </BaseTableContainer.Header.Left>

        <BaseTableContainer.Header.Right>
          <ClearFilterButton />
        </BaseTableContainer.Header.Right>
      </BaseTableContainer.Header>

      <BaseTableContainer.TableWrapper>
        <BaseTable columns={columns} data={catalog ?? []} />
      </BaseTableContainer.TableWrapper>

      <BaseTableContainer.Footer>
        <BaseTableContainer.Footer.Left />
        <BaseTableContainer.Footer.Right>
          <BasePagination>
            <BasePagination.Pages pagesCount={pageCount ?? 0} setSearchParams={setPaginationParams} />
            <BasePagination.Limit total={total ?? 0} setSearchParams={setPaginationParams} />
          </BasePagination>
        </BaseTableContainer.Footer.Right>
      </BaseTableContainer.Footer>
    </BaseTableContainer>
  );
};
